import {
  Badge,
  Container,
  EmptyState,
  Flex,
  Heading,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { FiSearch } from "react-icons/fi"
import { z } from "zod"

import { Vendors as VendorsClient } from "@/client"
import EditVendor from "@/components/Vendors/EditVendor"
import DeleteVendor from "@/components/Vendors/DeleteVendor"
import AddVendor from "@/components/Vendors/AddVendor"
import PendingVendors from "@/components/Vendors/PendingVendors"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination.tsx"

const VendorsSearchSchema = z.object({
  page: z.number().catch(1),
})

const PER_PAGE = 5

function getVendorsQueryOptions({ page }: { page: number }) {
  return {
    queryFn: () =>
      VendorsClient.readVendors({ query: { skip: (page - 1) * PER_PAGE, limit: PER_PAGE } }),
    queryKey: ["vendors", { page }],
  }
}

export const Route = createFileRoute("/_layout/vendors")({
  component: Vendors,
  validateSearch: (search) => VendorsSearchSchema.parse(search),
})

function VendorsTable() {
  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getVendorsQueryOptions({ page }),
    placeholderData: (prevData) => prevData,
  })

  const setPage = (page: number) =>
    navigate({
      search: (prev: { page: number }) => ({ ...prev, page }),
    })

  const vendors = data?.data.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0

  if (isLoading) {
    return <PendingVendors />
  }

  if (vendors.length === 0) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>You don't have any vendors yet</EmptyState.Title>
            <EmptyState.Description>
              Add a new vendor to get started
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <>
      <Table.Root size="sm" variant="outline">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>ABN</Table.ColumnHeader>
            <Table.ColumnHeader>Entity Name</Table.ColumnHeader>
            <Table.ColumnHeader>ABN Status</Table.ColumnHeader>
            <Table.ColumnHeader>Entity Type</Table.ColumnHeader>
            <Table.ColumnHeader>Location</Table.ColumnHeader>
            <Table.ColumnHeader w="80px" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {vendors?.map((vendor) => (
            <Table.Row key={vendor.id} opacity={isPlaceholderData ? 0.5 : 1}>
              <Table.Cell>
                {vendor.ABN ? (
                  <Badge colorPalette="gray" variant="outline" fontFamily="mono" size="sm">{vendor.ABN}</Badge>
                ) : (
                  <span style={{ color: "#A0AEC0" }}>—</span>
                )}
              </Table.Cell>
              <Table.Cell fontWeight="medium">
                {vendor.entityName || <span style={{ color: "#A0AEC0" }}>—</span>}
              </Table.Cell>
              <Table.Cell>
                {vendor.ABNStatus ? (
                  <Badge colorPalette={vendor.ABNStatus.toLowerCase() === "active" ? "green" : "gray"} size="sm">
                    {vendor.ABNStatus}
                  </Badge>
                ) : (
                  <span style={{ color: "#A0AEC0" }}>—</span>
                )}
              </Table.Cell>
              <Table.Cell color={!vendor.entityType ? "gray.400" : "inherit"}>
                {vendor.entityType || "—"}
              </Table.Cell>
              <Table.Cell color={!vendor.mainBusinessLocation ? "gray.400" : "inherit"}>
                {vendor.mainBusinessLocation ? (
                  <Text fontSize="sm">{vendor.mainBusinessLocation}</Text>
                ) : "—"}
              </Table.Cell>
              <Table.Cell>
                <Flex gap={1}>
                  <EditVendor vendor={vendor} iconOnly />
                  <DeleteVendor id={vendor.id} iconOnly />
                </Flex>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
      <Flex justifyContent="flex-end" mt={4}>
        <PaginationRoot
          count={count}
          pageSize={PER_PAGE}
          onPageChange={({ page }) => setPage(page)}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </>
  )
}

function Vendors() {
  return (
    <Container maxW="full">
      <Heading size="lg" pt={12}>
        Vendors Management
      </Heading>
      <AddVendor />
      <VendorsTable />
    </Container>
  )
}